/**
 * EVC Multiply Dialog
 *
 * This dialog uses Euler's full wagmi-evc stack for multiply operations.
 */

import { useWrongNetwork } from '@/domain/hooks/useWrongNetwork'
import { CommonDialogProps, DialogConfig } from '@/features/dialogs/common/types'
import { Dialog, DialogContent } from '@/ui/atoms/dialog/Dialog'
import { type Address } from 'viem'
import { EvcMultiplyDialogContentContainer } from './EvcMultiplyDialogContentContainer'

export interface EvcMultiplyDialogProps extends CommonDialogProps {
  collateralVault: Address
  collateralSymbol: string
  collateralAmount: string
  collateralDecimals: number
  collateralAsset: Address
  collateralLogoURI?: string
  liabilityVault: Address
  liabilitySymbol: string
  borrowAmount: string
  liabilityDecimals: number
  liabilityAsset: Address
  liabilityLogoURI?: string
  /** Sub-account ID (0-255). 0 = main account. */
  subAccountId?: number
  /** If true, wrap native HYPE → WHYPE for collateral deposit */
  wrapCollateralFromNative?: boolean
  /** Multiplier for leverage (e.g., 2x) */
  multiplier: number
  /** Slippage tolerance for swap operations (in percentage, e.g., 0.5 = 0.5%) */
  slippage: number
  /** Vault's total assets (for share conversion) */
  totalAssets?: bigint
  /** Vault's total shares (for share conversion) */
  totalShares?: bigint
  /** If true, user is adjusting an existing position (vs creating new) */
  isModifyingExistingPosition: boolean
}

function EvcMultiplyDialog({
  open,
  setOpen,
  collateralVault,
  collateralSymbol,
  collateralAmount,
  collateralDecimals,
  collateralAsset,
  collateralLogoURI,
  liabilityVault,
  liabilitySymbol,
  borrowAmount,
  liabilityDecimals,
  liabilityAsset,
  liabilityLogoURI,
  subAccountId,
  wrapCollateralFromNative,
  multiplier,
  slippage,
  totalAssets,
  totalShares,
  isModifyingExistingPosition,
}: EvcMultiplyDialogProps) {
  const isWrongNetwork = useWrongNetwork()

  if (isWrongNetwork) {
    setOpen(false)
    return null
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent aria-describedby={undefined}>
        <EvcMultiplyDialogContentContainer
          closeDialog={() => setOpen(false)}
          collateralVault={collateralVault}
          collateralSymbol={collateralSymbol}
          collateralAmount={collateralAmount}
          collateralDecimals={collateralDecimals}
          collateralAsset={collateralAsset}
          collateralLogoURI={collateralLogoURI}
          liabilityVault={liabilityVault}
          liabilitySymbol={liabilitySymbol}
          borrowAmount={borrowAmount}
          liabilityDecimals={liabilityDecimals}
          liabilityAsset={liabilityAsset}
          liabilityLogoURI={liabilityLogoURI}
          subAccountId={subAccountId}
          wrapCollateralFromNative={wrapCollateralFromNative}
          multiplier={multiplier}
          slippage={slippage}
          totalAssets={totalAssets}
          totalShares={totalShares}
          isModifyingExistingPosition={isModifyingExistingPosition}
        />
      </DialogContent>
    </Dialog>
  )
}

export const evcMultiplyDialogConfig: DialogConfig<EvcMultiplyDialogProps> = {
  options: {
    closeOnChainChange: true,
  },
  element: EvcMultiplyDialog,
}

export default EvcMultiplyDialog
